import {Box, Paper, Stack} from "@mui/material";
import {useTheme} from "@mui/material/styles";
import React from "react";
import SurveyRunnerMarkdown from "./SurveyRunnerMarkdown";
import SurveyRunnerScreenTitle from "./SurveyRunnerScreenTitle";
import {surveyControls, surveyQuestionLayout} from "./surveyRunnerColors";
import SurveyTextOn from "./SurveyTextOn";

const ACCENT_BAR_HEIGHT = 8;

/** Plain keeps the accent bar as a short rule above the welcome. */
const ACCENT_RULE_WIDTH = 48;

interface Props {
    title: string
    description: string
}

function SurveyRunnerIntroCard({title, description}: Props) {
    const {plain} = surveyQuestionLayout(useTheme());

    if (!title.trim() && !description.trim()) return null;

    const text = (
        <Stack gap={1}>
            {!!title.trim() && (
                <SurveyRunnerScreenTitle title={title}/>
            )}
            {!!description.trim() && (
                <SurveyRunnerMarkdown markdown={description} fontSize={16} color={'text.secondary'}/>
            )}
        </Stack>
    );

    // Plain: the welcome sits on the page like the questions below it, with the rule they have between them.
    if (plain) {
        return (
            <Box sx={{borderBottom: '1px solid', borderColor: 'divider', pb: {xs: 2.5, sm: 3}}}>
                <Box
                    aria-hidden
                    sx={{
                        width: ACCENT_RULE_WIDTH,
                        height: ACCENT_BAR_HEIGHT,
                        mb: 2,
                        borderRadius: 1,
                        // The contrast-checked accent: the brand colour itself can vanish on the page.
                        bgcolor: theme => surveyControls(theme).focus,
                    }}
                />
                {text}
            </Box>
        );
    }

    return (
        <SurveyTextOn on={'surface'}>
            <Paper
                elevation={0}
                sx={theme => ({
                    p: {xs: 2.5, sm: 3},
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderTop: `${ACCENT_BAR_HEIGHT}px solid ${surveyControls(theme).focus}`,
                })}
            >
                {text}
            </Paper>
        </SurveyTextOn>
    );
}

export default SurveyRunnerIntroCard;
